import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Spin, Tabs, type TabsProps } from 'antd';

import {
    TPO_EVENTS_CANCEL_BTN,
    TPO_EVENTS_LOADING,
    TPO_EVENTS_PAGE,
    TPO_EVENTS_SAVE_BTN,
    TPO_EVENTS_TABS,
    tpoEventsTabTestId,
} from '../../config/testIds';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import type { DimSelectFieldConfig } from '../../config/tpoGenConfig';
import { buildEventPayload } from '../../utils/buildEventPayload';
import { usePlanDetailsContext } from '../context/PlanDetailsContext';
import { useEditSession } from '../EditSession/EditSessionContext';
import PromoTab, { type PromoTabHandle } from '../Promo/PromoTab';
import useCreateEventMutation from './hooks/useCreateEventMutation';
import type { CreateEventPayload } from './hooks/useCreateEventMutation';
import useGetEventData from './hooks/useGetEventData';
import useEventDirtyState from './hooks/ui/useEventDirtyState';
import EventsSection from './EventsSection';
import { useRuleBoundForm } from '../../rules/store/useRuleBoundForm';
import { useControlState, useRuleStore } from '../../rules/store/selectors';
import { useSyncRuleContext } from '../../rules/context/useRuleContext';
import UnsavedChangesModal from './components/UnsavedChangesModal';
import DeletePromoModal from './components/DeletePromoModal';
import NoPromosModal from './components/NoPromosModal';
import {
    TPOGEN_EVENT_DISCARDED,
    TPOGEN_EVENT_PRODUCT_PORTFOLIO_CHANGED,
    TPOGEN_EVENT_PRODUCT_PORTFOLIO_SELECTED,
    TPOGEN_EVENT_UPDATED,
    TPOGEN_PROMOTION_ADD_CLICKED,
    TPOGEN_PROMOTION_DISCARDED,
} from 'utils/mixpanelEventNames';
import { trackTpoGenEvent, TPOGEN_PAGE_NAMES } from '../../utils/trackTpoGenEvent';

const ADD_PROMO_TAB_KEY = 'add-promo';

/** Classifies which kind of field changed for `change_type` on event-update events. */
const classifyEventChangeType = (
    touchedFieldNames: Set<string>,
    lowestFieldName: string | undefined,
    dateFieldNames: Set<string>,
): 'scope' | 'dates' | 'name' => {
    if (lowestFieldName && touchedFieldNames.has(lowestFieldName)) return 'scope';
    for (const fieldName of touchedFieldNames) {
        if (dateFieldNames.has(fieldName)) return 'dates';
    }
    return 'name';
};
const PROMO_TAB_LOADING = (
    <div className="flex justify-center items-center py-12" data-testid={TPO_EVENTS_LOADING}>
        <Spin size="default" />
    </div>
);
const editPromoTabKey = (promoId: string) => `edit-promo-${promoId}`;

type EventsViewProps = {
    planId: string;
};

type EditPromoEntry = {
    promoId: string;
    label: string;
};

const EventsView = ({ planId }: EventsViewProps) => {
    const tpoGenConfig = useTpoGenConfig();
    const { eventDetailsFields, promoDetailsFields, carryEventDatesToPromo, requirePromoForEvent } = tpoGenConfig;
    const {
        setActiveView,
        editEventId,
        setEditEventId,
        editPromoId,
        setEditPromoId,
        autoAddPromo,
        setAutoAddPromo,
        setHasUnsavedNoPromoEvent,
    } = usePlanDetailsContext();
    const { canEdit } = useEditSession();

    const isEditMode = !!editEventId;
    const [eventForm] = Form.useForm();

    const { derivedStates, onValuesChange: ruleOnValuesChange, syncToStore } = useRuleBoundForm('event', eventForm);

    const [eventId, setEventId] = useState<string | null>(editEventId);

    // Mirror the event's saved state into the rule store so `ctx:hasEntityId` conditions
    // (e.g. locking identity fields once the event exists) evaluate for the event scope.
    // `eventId` is seeded from `editEventId`, so this is truthy in edit flow too.
    useSyncRuleContext({ hasEntityId: !!eventId });

    const [promoTabCount, setPromoTabCount] = useState(0);
    const [savedPromoIds, setSavedPromoIds] = useState<Record<string, string>>({});
    const [promoDirtyMap, setPromoDirtyMap] = useState<Record<string, boolean>>({});
    const [activeTabKey, setActiveTabKey] = useState('1');
    const [openEditPromos, setOpenEditPromos] = useState<EditPromoEntry[]>([]);
    const [removedTabs, setRemovedTabs] = useState<Set<string>>(new Set());
    const [pendingCloseTabKey, setPendingCloseTabKey] = useState<string | null>(null);
    // Target tab the user tried to switch to while the active (promo or event) tab had unsaved
    // changes; distinct from pendingCloseTabKey which tracks closing a tab via its "x" button.
    const [pendingTargetTabKey, setPendingTargetTabKey] = useState<string | null>(null);
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);
    const [showNoPromosModal, setShowNoPromosModal] = useState(false);
    const [activeEventView, setActiveEventView] = useState<'event-details' | 'promo-details'>('event-details');
    const [deletePromoId, setDeletePromoId] = useState<string | null>(null);

    // Create-mode only: names of event fields the user has genuinely edited (see
    // handleEventValuesChange). Lets useEventDirtyState ignore rule-engine defaults
    // (e.g. Status auto-filled to Draft on mount) that were never touched by hand.
    const [touchedEventFieldNames, setTouchedEventFieldNames] = useState<Set<string>>(new Set());

    // JSON snapshot of form values at the moment the create-mode save completes.
    // Compared against live form values each render to detect post-save user edits
    const [savedSnapshotJson, setSavedSnapshotJson] = useState<string | null>(null);

    // Imperative handles for each mounted promo tab, keyed by tab key, so a tab switch away
    // from a dirty promo can discard its unsaved changes without lifting form state up here.
    const promoTabRefs = useRef<Record<string, PromoTabHandle | null>>({});

    const rawEventFormValues = Form.useWatch([], eventForm);
    const eventFormValues: Record<string, unknown> = useMemo(() => rawEventFormValues ?? {}, [rawEventFormValues]);

    const {
        formValues: fetchedEventFormValues,
        eventPromos,
        isPending: isEventDataPending,
        isFetching: isEventDataFetching,
    } = useGetEventData(isEditMode ? editEventId : null);

    // Covers both the initial load (`isPending`) AND any background refetch of the
    // same event (`isFetching`) — e.g. the refetch triggered by invalidating the
    // event-details query after a successful Update. `isPending` alone stays false
    // once data has been cached once, so it misses that post-update refetch window.
    const isEventDataLoading = isEventDataPending || isEventDataFetching;

    // Event-level aggregate over the currently-saved promos, mirrored into the rule
    // engine's ctx so `control:*` rules can gate the "Add tactic" affordance on the
    // event's promo COLLECTION (not just the one open tab). These are mechanic
    // VALUES (not labels) — see useGetEventData's PromoEntry.
    const existingPromoMechanics = useMemo(
        () =>
            eventPromos
                .map((p) =>
                    typeof p.attributes?.mechanic === 'string'
                        ? p.attributes.mechanic
                        : p.promo_mechanic,
                )
                .filter((m): m is string => !!m),
        [eventPromos],
    );
    const setRuleContext = useRuleStore((s) => s.setContext);
    const existingPromoMechanicsKey = existingPromoMechanics.join('|');
    useEffect(() => {
        setRuleContext({ existingPromoMechanics, savedPromoCount: eventPromos.length });
        // Re-sync on the array's CONTENT signature (not its identity) + the count.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setRuleContext, existingPromoMechanicsKey, eventPromos.length]);

    // Rule-driven disable for the "Add tactic" affordance (target-only
    // `control:addPromo`). OR'd with `!isEventSaved` at every call site.
    const isAddPromoDisabledByRule = useControlState('addPromo').disabled;

    const { mutate: mutateEvent, isPending: isEventMutationPending } = useCreateEventMutation({
        onSuccess: (data) => {
            const savedEventId = data?.event_id ?? data?.eventId ?? '';
            if (isEditMode) {
                trackTpoGenEvent(TPOGEN_EVENT_UPDATED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                    event_id: savedEventId,
                    change_type: classifyEventChangeType(touchedEventFieldNames, lowestFieldName, dateFieldNames),
                });
            }
            setEventId(savedEventId);
            setEditEventId(savedEventId);
            setSavedSnapshotJson(JSON.stringify(eventForm.getFieldsValue()));
            setActiveEventView('event-details');
        },
    });

    const isPromoTab = activeTabKey !== '1' && activeTabKey !== ADD_PROMO_TAB_KEY;

    const eventFieldConfigs = useMemo(
        () => [...eventDetailsFields, ...promoDetailsFields],
        [eventDetailsFields, promoDetailsFields],
    );

    // The lowest-attribute dimSelect field IS the "Product Portfolio" selector — reused
    // for building the save payload and for classifying which field changed for analytics.
    const lowestFieldName = useMemo(() => {
        const lowestField = promoDetailsFields.find(
            (field): field is DimSelectFieldConfig => field.type === 'dimSelect' && Boolean(field.lowestAttribute),
        );
        return lowestField ? lowestField.name ?? `${lowestField.dimension}-${lowestField.attributeType}` : undefined;
    }, [promoDetailsFields]);

    const dateFieldNames = useMemo(
        () =>
            new Set(
                eventDetailsFields
                    .filter((field) => field.type === 'datepicker')
                    .map((field) => field.name)
                    .filter((name): name is string => !!name),
            ),
        [eventDetailsFields],
    );

    // Event's current date values keyed by apiKey, carried into a new promo's
    // (disabled) date fields. Matched to promo date fields by apiKey in PromoTab.
    // Config-gated per customer via `carryEventDatesToPromo`; returns undefined
    // when off so PromoTab's carry effect is a guaranteed no-op for other customers.
    const eventDatesByApiKey = useMemo(() => {
        if (!carryEventDatesToPromo) return undefined;
        const dates: Record<string, unknown> = {};
        for (const field of eventDetailsFields) {
            if (field.type === 'datepicker' && field.apiKey && field.name) {
                dates[field.apiKey] = eventFormValues[field.name];
            }
        }
        return dates;
    }, [carryEventDatesToPromo, eventDetailsFields, eventFormValues]);

    const disabledFieldNames = useMemo(
        () =>
            new Set(
                Object.entries(derivedStates)
                    .filter(([, s]) => s.disabled)
                    .map(([k]) => k),
            ),
        [derivedStates],
    );

    const hiddenFieldNames = useMemo(
        () =>
            new Set(
                Object.entries(derivedStates)
                    .filter(([, s]) => s.visible === false)
                    .map(([k]) => k),
            ),
        [derivedStates],
    );

    const { isDirty: isEventFormDirty, canSave: canSaveEvent } = useEventDirtyState({
        fieldConfigs: eventFieldConfigs,
        formValues: eventFormValues,
        fetchedFormValues: fetchedEventFormValues ?? null,
        isEditMode,
        disabledFieldNames,
        hiddenFieldNames,
        touchedFieldNames: touchedEventFieldNames,
    });

    const isEventSaved = !!eventId;
    const hasAnySavedPromo = eventPromos.length > 0 || Object.keys(savedPromoIds).length > 0;
    const isActivePromoDirty = isPromoTab && !!promoDirtyMap[activeTabKey];

    // In create mode after a save: only re-enable Save if the user has actually
    // changed something compared to the snapshot captured at save time.
    const isFormChangedAfterSave = useMemo(() => {
        if (isEditMode || !savedSnapshotJson) return false;
        return JSON.stringify(eventFormValues) !== savedSnapshotJson;
    }, [isEditMode, savedSnapshotJson, eventFormValues]);

    const isEventSaveDisabled =
        !canEdit || !canSaveEvent || (!isEditMode && isEventSaved && !isFormChangedAfterSave);

    const isClosingTab = !!pendingCloseTabKey;
    const modalTitle = isClosingTab
        ? tpoGenConfig.labels.modals.unsavedPromoChanges
        : isPromoTab
        ? tpoGenConfig.labels.modals.unsavedPromoChanges
        : tpoGenConfig.labels.modals.unsavedEventChanges;
    const modalDescription =
        isClosingTab || isPromoTab
            ? tpoGenConfig.labels.modals.unsavedPromoDescription
            : tpoGenConfig.labels.modals.unsavedEventDescription;

    const navigateToCalendar = useCallback(() => {
        setEditEventId(null);
        setEditPromoId(null);
        setAutoAddPromo(false);
        setActiveView('calendar');
    }, [setEditEventId, setEditPromoId, setAutoAddPromo, setActiveView]);

    const handleAddPromo = useCallback(() => {
        trackTpoGenEvent(TPOGEN_PROMOTION_ADD_CLICKED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
            event_id: eventId ?? editEventId,
            change_type: 'add_promo_clicked',
        });
        const newIndex = promoTabCount + 1;
        const newTabKey = `promo-${newIndex}`;
        setPromoTabCount(newIndex);
        setActiveTabKey(newTabKey);
    }, [promoTabCount, eventId, editEventId]);

    const handleClose = () => {
        setAutoAddPromo(false);
        if (requirePromoForEvent && isEventSaved && !hasAnySavedPromo) {
            setShowNoPromosModal(true);
            return;
        }
        if (isPromoTab ? isActivePromoDirty : isEventFormDirty) {
            setShowUnsavedModal(true);
        } else {
            navigateToCalendar();
        }
    };

    const handleEventSave = async () => {
        try {
            await eventForm.validateFields();
            const formValues = eventForm.getFieldsValue();
            const configPayload = buildEventPayload(tpoGenConfig.entityAttributes, eventDetailsFields, formValues);
            const products = (lowestFieldName ? (formValues[lowestFieldName] as (string | number)[]) : []) ?? [];

            mutateEvent({
                ...configPayload,
                plan_id: planId,
                event_id: eventId,
                lowest_dim_attr_id: products.map(Number),
            } as CreateEventPayload);
        } catch (errorInfo) {
            // eslint-disable-next-line no-console
            console.error('Validation failed:', errorInfo);
        }
    };

    const handleDeletePromo = useCallback((promoId: string) => {
        setDeletePromoId(promoId);
    }, []);

    const handlePromoSaved = useCallback((tabKey: string, promotionId: string) => {
        setSavedPromoIds((prev) => ({ ...prev, [tabKey]: promotionId }));
    }, []);

    const handlePromoDirtyChange = useCallback((tabKey: string, isDirty: boolean) => {
        setPromoDirtyMap((prev) => ({ ...prev, [tabKey]: isDirty }));
    }, []);

    // Wraps ruleOnValuesChange so we also record which fields the user actually
    // edited. antd's `onValuesChange` only fires for real user input, not for
    // `form.setFieldsValue` writes made by the rule engine (autofills/resets),
    // so this set never picks up e.g. the Status -> Draft mount-time default.
    const handleEventValuesChange = useCallback(
        (changed: Record<string, unknown>) => {
            ruleOnValuesChange(changed);
            if (lowestFieldName && lowestFieldName in changed) {
                const eventIdForTracking = eventId ?? editEventId ?? undefined;
                if (isEditMode) {
                    trackTpoGenEvent(TPOGEN_EVENT_PRODUCT_PORTFOLIO_CHANGED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                        event_id: eventIdForTracking,
                        change_type: 'edit',
                    });
                } else {
                    trackTpoGenEvent(TPOGEN_EVENT_PRODUCT_PORTFOLIO_SELECTED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                        event_id: eventIdForTracking,
                        change_type: 'created',
                    });
                }
            }
            setTouchedEventFieldNames((prev) => {
                const next = new Set(prev);
                for (const key of Object.keys(changed)) next.add(key);
                return next;
            });
        },
        [ruleOnValuesChange, lowestFieldName, isEditMode, eventId, editEventId],
    );

    const removeTab = useCallback(
        (targetKey: string) => {
            if (targetKey.startsWith('edit-promo-')) {
                const promoId = targetKey.replace('edit-promo-', '');
                setOpenEditPromos((prev) => prev.filter((e) => e.promoId !== promoId));
                if (editPromoId === promoId) {
                    setEditPromoId(null);
                }
            } else {
                setRemovedTabs((prev) => new Set(prev).add(targetKey));
            }
            setPromoDirtyMap((prev) => {
                const next = { ...prev };
                delete next[targetKey];
                return next;
            });
            setSavedPromoIds((prev) => {
                const next = { ...prev };
                delete next[targetKey];
                return next;
            });
            delete promoTabRefs.current[targetKey];
            if (activeTabKey === targetKey) {
                setActiveTabKey('1');
            }
        },
        [activeTabKey, editPromoId, setEditPromoId],
    );

    // A deleted promo must not keep a tab open. Both tab flavours can point at it:
    // the `edit-promo-{id}` tab and a `promo-{n}` tab whose id landed in savedPromoIds
    // after being created in this session. Routed through removeTab (not a direct
    // openEditPromos filter) because removeTab also clears the context's editPromoId —
    // left set, the open-edit-tab effect below re-adds the tab on the next event refetch.
    const handlePromoDeleted = useCallback(
        (deletedPromoId: string) => {
            removeTab(editPromoTabKey(deletedPromoId));
            const newPromoTabKey = Object.keys(savedPromoIds).find((key) => savedPromoIds[key] === deletedPromoId);
            if (newPromoTabKey) removeTab(newPromoTabKey);
        },
        [removeTab, savedPromoIds],
    );

    const handleTabChange = useCallback(
        (key: string) => {
            if (key === ADD_PROMO_TAB_KEY) {
                handleAddPromo();
                return;
            }
            if (key === activeTabKey) return;
            const activeIsDirty = isPromoTab ? isActivePromoDirty : isEventFormDirty;
            if (activeIsDirty) {
                setPendingTargetTabKey(key);
                setShowUnsavedModal(true);
                return;
            }
            setActiveTabKey(key);
        },
        [handleAddPromo, activeTabKey, isPromoTab, isActivePromoDirty, isEventFormDirty],
    );

    const handleTabEdit = useCallback(
        (targetKey: React.MouseEvent | React.KeyboardEvent | string, action: 'add' | 'remove') => {
            if (action === 'add') {
                handleAddPromo();
                return;
            }
            const key = targetKey as string;
            if (promoDirtyMap[key]) {
                setPendingCloseTabKey(key);
                setShowUnsavedModal(true);
                return;
            }
            removeTab(key);
        },
        [handleAddPromo, promoDirtyMap, removeTab],
    );

    const tabItems = useMemo(() => {
        const items: NonNullable<TabsProps['items']> = [
            {
                key: '1',
                label: <span data-testid={tpoEventsTabTestId('1')}>{tpoGenConfig.labels.events.eventDetails}</span>,
                closable: false,
                children: (
                    <EventsSection
                        form={eventForm}
                        isEventSaved={isEventSaved}
                        addPromoDisabled={isAddPromoDisabledByRule}
                        onAddPromo={handleAddPromo}
                        onDeletePromo={handleDeletePromo}
                        isEditMode={isEditMode}
                        fetchedFormValues={fetchedEventFormValues}
                        isEventDataLoading={isEventDataLoading}
                        eventPromos={eventPromos}
                        activeEventView={activeEventView}
                        onActiveEventViewChange={setActiveEventView}
                        derivedStates={derivedStates}
                        onValuesChange={handleEventValuesChange}
                        syncToStore={syncToStore}
                    />
                ),
            },
        ];

        for (const entry of openEditPromos) {
            const tabKey = editPromoTabKey(entry.promoId);
            items.push({
                key: tabKey,
                label: <span data-testid={tpoEventsTabTestId(tabKey)}>{entry.label}</span>,
                closable: true,
                children: isEventDataLoading ? PROMO_TAB_LOADING : (
                    <PromoTab
                        ref={(el) => {
                            promoTabRefs.current[tabKey] = el;
                        }}
                        planId={planId}
                        eventId={eventId ?? ''}
                        instanceId={tabKey}
                        promotionId={entry.promoId}
                        isActive={tabKey === activeTabKey}
                        eventDatesByApiKey={eventDatesByApiKey}
                        onDirtyChange={(isDirty) => handlePromoDirtyChange(tabKey, isDirty)}
                    />
                ),
            });
        }

        let unsavedTabCount = 0;
        for (let i = 1; i <= promoTabCount; i++) {
            const tabKey = `promo-${i}`;
            if (removedTabs.has(tabKey)) continue;
            const savedId = savedPromoIds[tabKey];

            const matchedPromo = savedId ? eventPromos.find((p) => p.promo_id === savedId) : null;

            let label: string;
            if (matchedPromo) {
                label = matchedPromo.promo_name || `${tpoGenConfig.labels.promos.promo} ${eventPromos.length}`;
            } else {
                unsavedTabCount++;
                label = `${tpoGenConfig.labels.promos.promo} ${eventPromos.length + unsavedTabCount}`;
            }

            items.push({
                key: tabKey,
                label: <span data-testid={tpoEventsTabTestId(tabKey)}>{label}</span>,
                closable: true,
                children: (isEditMode && isEventDataLoading) ? PROMO_TAB_LOADING : (
                    <PromoTab
                        ref={(el) => {
                            promoTabRefs.current[tabKey] = el;
                        }}
                        planId={planId}
                        eventId={eventId ?? ''}
                        instanceId={tabKey}
                        promotionId={savedId}
                        isActive={tabKey === activeTabKey}
                        eventDatesByApiKey={eventDatesByApiKey}
                        onPromoSaved={(id) => handlePromoSaved(tabKey, id)}
                        onDirtyChange={(isDirty) => handlePromoDirtyChange(tabKey, isDirty)}
                    />
                ),
            });
        }

        const isAddPromoDisabled = !isEventSaved || isAddPromoDisabledByRule;
        items.push({
            key: ADD_PROMO_TAB_KEY,
            label: (
                <span
                    data-testid={tpoEventsTabTestId(ADD_PROMO_TAB_KEY)}
                    data-state-disabled={isAddPromoDisabled ? 'true' : undefined}
                >
                    {tpoGenConfig.labels.promos.addPromoTab}
                </span>
            ),
            closable: false,
            children: <></>,
            disabled: isAddPromoDisabled,
        });

        return items;
    }, [
        promoTabCount,
        planId,
        eventId,
        isEventSaved,
        isAddPromoDisabledByRule,
        eventForm,
        handleAddPromo,
        handleDeletePromo,
        handlePromoSaved,
        handlePromoDirtyChange,
        savedPromoIds,
        removedTabs,
        isEditMode,
        openEditPromos,
        fetchedEventFormValues,
        isEventDataLoading,
        eventPromos,
        activeEventView,
        activeTabKey,
        derivedStates,
        handleEventValuesChange,
        syncToStore,
        eventDatesByApiKey,
    ]);

    useEffect(() => {
        if (editPromoId) {
            const promo = eventPromos.find((p) => p.promo_id === editPromoId);
            const label = promo?.promo_name || tpoGenConfig.labels.promos.editPromo;

            setOpenEditPromos((prev) => {
                const alreadyOpen = prev.some((e) => e.promoId === editPromoId);
                if (!alreadyOpen) {
                    return [...prev, { promoId: editPromoId, label }];
                }
                // Update label in case eventPromos loaded after the tab was first opened
                return prev.map((e) => (e.promoId === editPromoId && e.label !== label ? { ...e, label } : e));
            });
            setActiveTabKey(editPromoTabKey(editPromoId));
        }
    }, [editPromoId, eventPromos]);

    useEffect(() => {
        if (autoAddPromo && isEventSaved) {
            handleAddPromo();
            setAutoAddPromo(false);
        }
    }, [autoAddPromo, isEventSaved, handleAddPromo, setAutoAddPromo]);

    useEffect(() => {
        setHasUnsavedNoPromoEvent(requirePromoForEvent && isEventSaved && !hasAnySavedPromo);
        return () => setHasUnsavedNoPromoEvent(false);
    }, [requirePromoForEvent, isEventSaved, hasAnySavedPromo, setHasUnsavedNoPromoEvent]);

    return (
        <div data-testid={TPO_EVENTS_PAGE}>
            <div className="flex flex-row justify-between items-center">
                <p className="text-base font-medium" data-testid={`${TPO_EVENTS_PAGE}_title`}>
                    {isEditMode ? tpoGenConfig.labels.events.editEvent : tpoGenConfig.labels.events.addNewEvent}
                </p>
                <div className="flex flex-row gap-x-2">
                    {!isPromoTab && (
                        <Button
                            type="primary"
                            onClick={handleEventSave}
                            disabled={isEventSaveDisabled}
                            loading={isEventMutationPending}
                            data-testid={TPO_EVENTS_SAVE_BTN}
                            data-state-disabled={isEventSaveDisabled ? 'true' : undefined}
                        >
                            {isEditMode || isEventSaved
                                ? tpoGenConfig.labels.events.update
                                : tpoGenConfig.labels.common.save}
                        </Button>
                    )}
                    <Button type="default" onClick={handleClose} data-testid={TPO_EVENTS_CANCEL_BTN}>
                        {tpoGenConfig.labels.common.close}
                    </Button>
                </div>
            </div>
            <div data-testid={TPO_EVENTS_TABS}>
                <Tabs
                    className="events-view-tabs"
                    type="editable-card"
                    activeKey={activeTabKey}
                    onChange={handleTabChange}
                    onEdit={handleTabEdit}
                    items={tabItems}
                    hideAdd
                />
            </div>
            <UnsavedChangesModal
                open={showUnsavedModal}
                title={modalTitle}
                description={
                    <div className="bg-[#fafafa] rounded-lg p-2">
                        <p className="text-sm text-[#18181b]">{modalDescription}</p>
                    </div>
                }
                onContinueEditing={() => {
                    if (pendingCloseTabKey) {
                        setActiveTabKey(pendingCloseTabKey);
                        setPendingCloseTabKey(null);
                    }
                    setPendingTargetTabKey(null);
                    setShowUnsavedModal(false);
                }}
                onDiscard={() => {
                    const discardedEventId = eventId ?? editEventId ?? undefined;
                    if (pendingCloseTabKey) {
                        // Only promo tabs are closable (the event-details tab has closable: false).
                        trackTpoGenEvent(TPOGEN_PROMOTION_DISCARDED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                            event_id: discardedEventId,
                            change_type: 'add_promo_discarded',
                        });
                        removeTab(pendingCloseTabKey);
                        setPendingCloseTabKey(null);
                        setShowUnsavedModal(false);
                    } else if (pendingTargetTabKey) {
                        if (isPromoTab) {
                            trackTpoGenEvent(TPOGEN_PROMOTION_DISCARDED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                                event_id: discardedEventId,
                                change_type: 'add_promo_discarded',
                            });
                            promoTabRefs.current[activeTabKey]?.reset();
                        } else {
                            if (!isEditMode) {
                                trackTpoGenEvent(TPOGEN_EVENT_DISCARDED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                                    plan_id: planId,
                                    change_type: 'add_event_discarded',
                                });
                            }
                            eventForm.resetFields();
                        }
                        setActiveTabKey(pendingTargetTabKey);
                        setPendingTargetTabKey(null);
                        setShowUnsavedModal(false);
                    } else {
                        if (isPromoTab) {
                            trackTpoGenEvent(TPOGEN_PROMOTION_DISCARDED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                                event_id: discardedEventId,
                                change_type: 'add_promo_discarded',
                            });
                        } else if (!isEditMode) {
                            trackTpoGenEvent(TPOGEN_EVENT_DISCARDED, TPOGEN_PAGE_NAMES.PLAN_DETAIL_EVENTS, {
                                plan_id: planId,
                                change_type: 'add_event_discarded',
                            });
                        }
                        eventForm.resetFields();
                        navigateToCalendar();
                    }
                }}
            />
            <DeletePromoModal
                open={!!deletePromoId}
                onClose={() => setDeletePromoId(null)}
                promoId={deletePromoId}
                promoName={eventPromos.find((p) => p.promo_id === deletePromoId)?.promo_name}
                eventId={eventId ?? ''}
                onSuccess={handlePromoDeleted}
            />
            <NoPromosModal
                open={showNoPromosModal}
                onClose={() => setShowNoPromosModal(false)}
                eventId={eventId}
                onDeleteSuccess={navigateToCalendar}
            />
        </div>
    );
};

EventsView.displayName = 'EventsView';
export default EventsView;
